// frontend/src/components/upload/DataPreview.jsx
import React from 'react';
import { Calendar, Zap, TrendingUp, Database, AlertTriangle, CheckCircle } from 'lucide-react';

const DataPreview = ({ statistics, preview, qualityReport }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(num));
  };

  const formatEnergy = (kwh) => {
    if (kwh > 1000000) {
      return `${(kwh / 1000000).toFixed(1)} GWh`;
    } else if (kwh > 1000) {
      return `${(kwh / 1000).toFixed(1)} MWh`;
    }
    return `${kwh.toFixed(1)} kWh`;
  };

  return (
    <div className="space-y-6">
      
      {/* Statistiques principales */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Points de données */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-600 rounded-lg p-2">
              <Database className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-blue-600">
              {formatNumber(statistics.total_points)}
            </span>
          </div>
          <div>
            <div className="font-medium text-blue-900">Points de mesure</div>
            <div className="text-sm text-blue-600">
              Qualité: {qualityReport.data_completeness.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Période couverte */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-green-600 rounded-lg p-2">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-green-600">
              {Math.ceil((new Date(statistics.end_date) - new Date(statistics.start_date)) / (1000 * 60 * 60 * 24))}j
            </span>
          </div>
          <div>
            <div className="font-medium text-green-900">Période analysée</div>
            <div className="text-sm text-green-600">
              {formatDate(statistics.start_date).split(',')[0]}
            </div>
          </div>
        </div>

        {/* Puissance max */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-orange-600 rounded-lg p-2">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-orange-600">
              {formatNumber(statistics.puissance_max_kw)}
            </span>
          </div>
          <div>
            <div className="font-medium text-orange-900">Puissance max</div>
            <div className="text-sm text-orange-600">kW</div>
          </div>
        </div>

        {/* Énergie totale */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-600 rounded-lg p-2">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-purple-600">
              {formatEnergy(statistics.energie_totale_kwh)}
            </span>
          </div>
          <div>
            <div className="font-medium text-purple-900">Énergie totale</div>
            <div className="text-sm text-purple-600">Consommation</div>
          </div>
        </div>
      </div>

      {/* Rapport de qualité simplifié */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          {qualityReport.is_valid ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          )}
          Rapport de Qualité
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {qualityReport.gaps_detected.length}
            </div>
            <div className="text-sm text-gray-600">Gaps détectés</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {qualityReport.outliers_detected.length}
            </div>
            <div className="text-sm text-gray-600">Outliers trouvés</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {qualityReport.data_completeness.toFixed(0)}%
            </div>
            <div className="text-sm text-gray-600">Complétude</div>
          </div>
        </div>

        {qualityReport.warnings.length > 0 && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="font-medium text-orange-800 mb-2">Avertissements:</div>
            <ul className="text-sm text-orange-700 space-y-1">
              {qualityReport.warnings.slice(0, 3).map((warning, index) => (
                <li key={index}>• {warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Aperçu des données */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Aperçu des données (5 premiers points)
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Puissance (kW)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualité
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {preview.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {formatDate(row.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.puissance_active_kw.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      row.qualite_mesure === 'REEL' 
                        ? 'bg-green-100 text-green-800'
                        : row.qualite_mesure === 'ESTIME'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {row.qualite_mesure || 'REEL'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommandations */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          📊 Recommandations pour l'analyse TURPE
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• Vos données couvrent une période suffisante pour une analyse précise</li>
          <li>• La qualité des données permet un calcul TURPE fiable</li>
          {statistics.puissance_max_kw > 36 && (
            <li>• Votre installation semble être en BT &gt; 36 kVA (tarification TURPE applicable)</li>
          )}
          <li>• Préparez vos données contractuelles (puissances souscrites, version d'utilisation)</li>
        </ul>
      </div>
    </div>
  );
};

export default DataPreview;
